import async from 'async';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';

import { default as Button, default as IconButton } from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';

//- local
import DeliveryEstimate from '../../components/DeliveryEstimate';
import ConfirmCancelDeliveryDialog from '../../components/Dialogs/ConfirmCancelDeliveryDialog';
import ConfirmDeliveryDialog from '../../components/Dialogs/ConfirmDeliveryDialog';
import UpdatingDeliveryDialog from '../../components/Dialogs/UpdatingDeliveryDialog';
import CarrierSelect from '../../components/form/CarrierSelect';
import ResourceUploader from '../../components/form/ResourceUploader';
import TerminalSupplierPicker from '../../components/pureForm/DeliveryTerminalSupplierPicker';
import FMPTimePicker from '../../components/pureForm/FMPTimePicker';
import FMPTextField from '../../components/ui/FMPTextField';
import CarriersContainer from '../../containers/logistics/Carriers';
import LoadingAssignmentsContainer from '../../containers/logistics/LoadingAssignments';
import DeliveryContainer from '../../containers/logistics/StationDelivery';
import TerminalsContainer from '../../containers/logistics/Terminals';
import StationsContainer from '../../containers/Stations';

//- Pureform
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import FMPProductSelect from '../../components/pureForm/FMPProductSelect';
import FMPSelect from '../../components/pureForm/FMPSelect';
import StationTankSelect from '../../components/pureForm/StationTankSelect';
import FMPRegionHeader from '../../components/ui/FMPRegionHeader';
import Note from '../../components/ui/Note';
import WarningIcon from '@material-ui/icons/Warning';

//- lib
import { valueToDisplay } from '../../libs/products';

const styles = (theme) => {
	const primaryColor = theme.palette.primary[500];
	return {
		sectionHeader: {
			margin: '30px 0px 10px 0px',
			textAlign: 'left',
			textTransform: 'uppercase',
			fontSize: 14,
			color: '#777',
			fontWeight: 900,
			letterSpacing: 2,
		},
		allDayContainer: {
			margin: '15px 0px 0px 0px',
			letterSpacing: 0,
		},
		sectionDivider: {
			backgroundColor: primaryColor,
		},
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
			boxSizing: 'border-box',
			padding: 25,
			marginTop: 20,
			[theme.breakpoints.up('md')]: {
				padding: '10px 40px 40px 40px',
			},
		},
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
			marginBottom: 30,
			textTransform: 'uppercase',
		},
		submitButton: {
			fontSize: 18,
			borderRadius: '100px',
			padding: '20px 30px',
			textTransform: 'uppercase',
		},
		formControl: {
			width: '100%',
			margin: '10px 0',
		},
		splitLoadSection: {
			transition: 'max-height .5s ease-in-out',
			overflow: 'hidden',
			maxHeight: 0,
		},
		terminalGroup: {
			borderLeft: '5px solid #ccc',
			marginTop: 10,
			marginBottom: 20,
			paddingLeft: 10,
			paddingTop: 10,
		},
		terminalSection: {
			paddingBottom: 30,
		},
		pickup: {
			margin: '15px 0px',
		},
		allTechAlert: {
			fontSize: 18,
			padding: 20,
			border: '2px solid #f44336',
			margin: 20,
			backgroundColor: 'white',
		},
		pickFuelType: {
			padding: 20,
			fontSize: 20,
			margin: '20px 10px',
			border: '1px solid #ddd',
			backgroundColor: '#f5f5f5',
			color: '#666',
		},
		errorIcon: {
			marginTop: -9,
			width: 26,
			height: 26,
			position: 'relative',
			bottom: -7,
			marginRight: 3,
			color: '#F4511E',
		},
		warningIcon: {
			marginTop: -9,
			width: 26,
			height: 26,
			position: 'relative',
			bottom: -7,
			marginRight: 3,
			color: '#FBC02D',
		},
	};
};
const formFields = {
	primaryStationTank: {},
	primaryDeliveryAmount: '',
	secondaryStationTank: {},
	secondaryDeliveryAmount: '',
	startDate: '',
	startTime: '',
	endDate: '',
	endTime: '',
	carrierId: '',

	fuelPickupType: '',
	firstTerminalTerminalId: '',
	firstTerminalSupplierId: '',
	firstTerminalLoadingNumber: '',
	firstTerminalSecondaryLoadingNumber: '',
	firstTerminalAccountNumber: '',
	firstTerminalProductType: 'unl',

	firstTerminalEmailCopyOfDeliveryRequest: false,
	firstTerminalUseStationDeliveryIdAsLoadingNumber: false,
	firstTerminalRequireLoadingNumberPerStationOnSplitLoads: false,
	firstTerminalUseLoadingAssignments: false,
	firstTerminalCarrierNeedsToProvidePickupWindow: false,
	firstTerminalBackups: [],

	secondTerminalTerminalId: '',
	secondTerminalSupplierId: '',
	secondTerminalLoadingNumber: '',
	secondTerminalSecondaryLoadingNumber: '',
	secondTerminalAccountNumber: '',

	secondTerminalEmailCopyOfDeliveryRequest: false,
	secondTerminalUseStationDeliveryIdAsLoadingNumber: false,
	secondTerminalRequireLoadingNumberPerStationOnSplitLoads: false,
	secondTerminalUseLoadingAssignments: false,
	secondTerminalCarrierNeedsToProvidePickupWindow: false,
	secondTerminalBackups: [],

	attachmentResourceIds: [],
	note: '',
	internalNote: '',
	date: '',
	allDayWindow: false,
};

const ALL_TECH_TERMINAL_ID = 27;

function calcE85Blend(totalE85) {
	const etohNeeded = totalE85 * 0.85;
	const carbobNeeded = totalE85 * 0.15;
	const gallonsOfEtohInCarbob = carbobNeeded * 0.1;
	const carbobWith10PercentEtoh = (carbobNeeded + gallonsOfEtohInCarbob) * 0.1;
	let etoh = etohNeeded - carbobWith10PercentEtoh;
	let unl = carbobNeeded + carbobWith10PercentEtoh;

	if (_.round(etoh) > etoh && _.round(unl) > unl) {
		etoh = Math.ceil(etoh);
		unl = Math.floor(unl);
	} else {
		etoh = _.round(etoh);
		unl = _.round(unl);
	}

	return {
		calculatedEtoh: etoh,
		calculatedCarbob: unl,
	};
}

function calcAllTechBlend(totalE85) {
	let etoh = totalE85 * 0.815;
	let unl = totalE85 * 0.185;

	if (_.round(etoh) > etoh && _.round(unl) > unl) {
		etoh = Math.ceil(etoh);
		unl = Math.floor(unl);
	} else {
		etoh = _.round(etoh);
		unl = _.round(unl);
	}

	return {
		calculatedEtoh: etoh,
		calculatedCarbob: unl,
	};
}

class SaveStationDelivery extends Component {
	constructor(props) {
		super(props);
		const stationDeliveryId = _.get(this.props, ['match', 'params', 'stationDeliveryId']);
		const stationId = _.get(this.props, ['match', 'params', 'stationId']);
		const tankNumber = _.get(this.props, ['match', 'params', 'tankNumber']);
		const productType = _.get(this.props, ['match', 'params', 'productType']);

		this.state = {
			stationDeliveryId: stationDeliveryId ? parseInt(stationDeliveryId, 10) : undefined,
			isUpdate: stationDeliveryId,
			showSplitLoad: false,
			formFieldError: {},
			isConfirmDialogOpen: false,
			isUpdatingDialogOpen: false,
			isCancelDialogOpen: false,
			updatingStatus: 'pending',
			estimate: undefined,
			secondaryEstimate: undefined,
			isFetchingEstimate: false,
			isFetchingSecondaryEstimate: false,
			primaryStationChanged: false,
			secondaryStationChanged: false,
			assignmentStats: [],
		};
		this.state = _.merge(this.state, formFields);

		if (stationId && tankNumber && productType) {
			this.state.primaryStationTank = {
				stationId: parseInt(stationId, 10),
				tankNumber: parseInt(tankNumber, 10),
				productType,
			};
		}
	}

	componentDidMount() {
		//- Only used for updates
		const {
			stationDelivery,
			getDelivery,
			stations,
			searchStations,
			carriers,
			searchCarriers,
			terminals,
			searchTerminals,
			getLoadingAssignmentStats,
		} = this.props;

		const { isUpdate, stationDeliveryId, primaryStationTank, assignmentStats } = this.state;

		if (!stations.length) {
			searchStations();
		}
		if (!carriers.length) {
			searchCarriers();
		}
		if (!terminals.length) {
			searchTerminals();
		}

		if (!assignmentStats.length) {
			getLoadingAssignmentStats({
				callback: (err, r) => {
					if (err) {
						return this.props.showErrors([err]);
					}

					/** @namespace r.assignmentsStats **/
					this.setState({ assignmentStats: r.assignmentStats });
				},
			});
		}

		//- Just an add. But we might start with a station
		if (!isUpdate) {
			if (!_.isEmpty(primaryStationTank)) {
				this.setState({ isFetchingEstimate: true });
			}
			return;
		}

		//- This is an update so fetch the delivery
		if (!stationDelivery) {
			getDelivery({
				params: { stationDeliveryIds: [stationDeliveryId] },
			});
			return;
		}

		//
		//- Its an update and we have the delivery info already
		//
		const newState = this.getFormValuesFromDelivery({ stationDelivery });
		newState.isFetchingEstimate = true;
		if (!_.isEmpty(newState.secondaryStationTank)) {
			newState.isFetchingSecondaryEstimate = true;
			newState.showSplitLoad = true;
		}
		this.setState(newState);
	}

	/**
	 * Checks and updates the estimate panels
	 */
	componentDidUpdate(prevProps, prevState) {
		const { stationDelivery } = this.props;

		//- isUpdate and fetching the delivery finished
		if (_.isUndefined(prevProps.stationDelivery) && stationDelivery) {
			const newState = this.getFormValuesFromDelivery({ stationDelivery });
			newState.isFetchingEstimate = true;
			if (!_.isEmpty(newState.secondaryStationTank)) {
				newState.isFetchingSecondaryEstimate = true;
				newState.showSplitLoad = true;
			}
			this.setState(newState);
		}

		//- Need to update the estimate
		if (prevState.isFetchingEstimate === false && this.state.isFetchingEstimate === true) {
			let primaryStationChanged = false;
			if (prevState.primaryStationChanged === false && this.state.primaryStationChanged === true) {
				primaryStationChanged = true;
			}

			this.getEstimateAndLastDelivery({
				isPrimary: true,
				primaryStationChanged,
			});
		}

		if (
			prevState.isFetchingSecondaryEstimate === false &&
			this.state.isFetchingSecondaryEstimate === true
		) {
			let secondaryStationChanged = false;
			if (
				prevState.secondaryStationChanged === false &&
				this.state.secondaryStationChanged === true
			) {
				secondaryStationChanged = true;
			}

			this.getEstimateAndLastDelivery({
				isSecondary: true,
				secondaryStationChanged,
			});
		}
	}

	getEstimateParams = (p) => {
		const { isPrimary, isSecondary } = p;

		let {
			startDate,
			startTime,
			endDate,
			endTime,
			primaryDeliveryAmount,
			secondaryDeliveryAmount,
			primaryStationTank,
			secondaryStationTank,
			allDayWindow,
		} = this.state;

		let params = {};

		if (isPrimary) {
			if (_.isEmpty(primaryStationTank)) {
				return false;
			}

			const { stationId, tankNumber, productType } = primaryStationTank;
			params = { stationId, tankNumber, productType };

			const deliveryAmount = _.parseInt(primaryDeliveryAmount, 10);
			if (deliveryAmount && deliveryAmount > 1000) {
				params.deliveryAmount = parseInt(primaryDeliveryAmount, 10);
			}
		}

		if (isSecondary) {
			if (_.isEmpty(secondaryStationTank)) {
				return false;
			}

			const { stationId, tankNumber, productType } = secondaryStationTank;
			params = { stationId, tankNumber, isSecondary: true, productType };

			const deliveryAmount = _.parseInt(secondaryDeliveryAmount, 10);
			if (deliveryAmount && deliveryAmount > 1000) {
				params.deliveryAmount = parseInt(secondaryDeliveryAmount, 10);
			}
		}

		//- window
		if (startDate && allDayWindow) {
			const deliveryWindowStart = moment(startDate).startOf('day').utc();
			const deliveryWindowEnd = moment(startDate).endOf('day').utc();
			params.deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			params.deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		} else if (startDate && startTime && endDate && endTime) {
			const sd = moment(startDate).format('YYYY-MM-DD');
			const st = moment(startTime).format('HH:mm');
			const deliveryWindowStart = moment(`${sd} ${st}`, 'YYYY-MM-DD HH:mm').utc();

			const ed = moment(endDate).format('YYYY-MM-DD');
			const et = moment(endTime).format('HH:mm');
			const deliveryWindowEnd = moment(`${ed} ${et}`, 'YYYY-MM-DD HH:mm').utc();

			params.deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			params.deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		}
		return params;
	};

	getEstimateAndLastDelivery = (p) => {
		const { isPrimary, isSecondary, primaryStationChanged, secondaryStationChanged } = p;

		const { estimateDelivery, showErrors } = this.props;
		const params = this.getEstimateParams({ isPrimary, isSecondary });

		const newState = {};
		if (!params) {
			if (isSecondary) {
				newState.isFetchingSecondaryEstimate = false;
				newState.secondaryStationChanged = false;
			}
			if (isPrimary) {
				newState.isFetchingEstimate = false;
				newState.primaryStationChanged = false;
			}
			this.setState(newState);
			return;
		}

		const {
			stationId,
			tankNumber,
			deliveryAmount,
			deliveryWindowStartDateTime,
			deliveryWindowEndDateTime,
		} = params;

		const formFieldError = _.clone(this.state.formFieldError);
		newState.formFieldError = formFieldError;
		['startTime', 'startDate', 'endTime', 'endDate'].forEach((s) => {
			delete formFieldError[s];
		});

		//- Check for form errors
		if (deliveryWindowStartDateTime && deliveryWindowEndDateTime) {
			const start = moment(deliveryWindowStartDateTime, 'YYYY-MM-DD HH:mm');
			const end = moment(deliveryWindowEndDateTime, 'YYYY-MM-DD HH:mm');
			if (start.isAfter(end)) {
				const error = 'Start date must be before end date';
				formFieldError['startTime'] = error;
				formFieldError['startDate'] = error;
			}
		}

		//- Error
		if (formFieldError['startTime']) {
			if (isSecondary) {
				newState.isFetchingSecondaryEstimate = false;
				newState.secondaryStationChanged = false;
			}
			if (isPrimary) {
				newState.isFetchingEstimate = false;
				newState.primaryStationChanged = false;
			}
			this.setState(newState);
			return;
		}

		async.waterfall(
			[
				(callback) => {
					setTimeout(callback, 300); //slight delay
				},
				(callback) => {
					let estimateParams = {
						stationIds: [stationId],
						deliveryAmount,
						deliveryWindowStartDateTime,
						deliveryWindowEndDateTime,
					};

					if (primaryStationChanged) {
						delete estimateParams.deliveryAmount;
						delete estimateParams.deliveryWindowStartDateTime;
						delete estimateParams.deliveryWindowEndDateTime;
					}

					if (secondaryStationChanged) {
						delete estimateParams.deliveryAmount;
					}

					estimateDelivery({
						params: estimateParams,
						callback,
					});
				},
				(r, callback) => {
					let estimate = _.find(r, { stationId, tankNumber }) || {};
					let newState = {};

					//- Go through the last StationDelivery and set values
					const {
						lastStationDelivery,
						recommendedDeliveryVolume,
						recommendedStartWindow,
						recommendedEndWindow,
					} = estimate;

					// Secondary estimate just updates the delivery amount
					if (isSecondary) {
						if (!this.state.secondaryDeliveryAmount) {
							if (recommendedDeliveryVolume) {
								newState.secondaryDeliveryAmount = recommendedDeliveryVolume;
							} else if (lastStationDelivery && lastStationDelivery.primaryDeliveryAmount) {
								newState.secondaryDeliveryAmount = lastStationDelivery.primaryDeliveryAmount;
							}
						}
						newState.secondaryEstimate = estimate;
						newState.isFetchingSecondaryEstimate = false;
						newState.secondaryStationChanged = false;

						this.setState(newState);
						return callback();
					}

					//
					//- Primary estimate. Things get toggled
					//

					//- If fuelPickupType is singleFuelPickup. The don't populate second terminal
					const { fuelPickupType, isUpdate } = this.state;

					//- Has a last stationDelivery
					if (lastStationDelivery) {
						[
							'primaryDeliveryAmount',
							'secondaryDeliveryAmount',
							'carrierId',

							'fuelPickupType',
							'firstTerminalProductType',
							'firstTerminalTerminalId',
							'firstTerminalSupplierId',
							'firstTerminalBackups',

							'secondTerminalTerminalId',
							'secondTerminalSupplierId',
							'secondTerminalBackups',

							'note',
							//'internalNote',
							'allDayWindow',
						].forEach((key) => {
							if (_.isNil(lastStationDelivery[key])) {
								return;
							}

							//- Primary station changed. Update everything
							if (primaryStationChanged) {
								newState[key] = lastStationDelivery[key];
								return;
							}

							//- Has value of false so it will always override to true
							if (key === 'allDayWindow') {
								return;
							}

							//- Field change don't override any settings we already have
							if (_.isArray(this.state[key]) && this.state[key].length) {
								return;
							}
							if (!_.isArray(this.state[key]) && this.state[key]) {
								return;
							}

							//- Do not populate second terminal info if singlePickupType
							if (fuelPickupType === 'singleFuelPickup') {
								if (
									key === 'secondTerminalTerminalId' ||
									key === 'secondTerminalSupplierId' ||
									key === 'secondTerminalBackups'
								) {
									return;
								}
							}

							if (isUpdate && (key === 'note' || key === 'internalNote')) {
								return;
							}

							newState[key] = lastStationDelivery[key];
						});

						//- If not set. Override the secondaryStation
						const {
							secondaryStationId,
							secondaryTankNumber,
							secondaryProductType,
						} = lastStationDelivery;
						if (_.isEmpty(this.state.secondaryStationTank) && secondaryStationId) {
							newState.secondaryStationTank = {
								stationId: secondaryStationId,
								tankNumber: secondaryTankNumber,
								productType: secondaryProductType,
							};

							if (primaryStationChanged) {
								newState.showSplitLoad = true;
								newState.isFetchingSecondaryEstimate = true;
							}
						}

						if (primaryStationChanged && this.state.showSplitLoad) {
							newState.isFetchingSecondaryEstimate = true;
						}
					}

					//- Only if we have an estimate
					if (recommendedStartWindow && recommendedEndWindow) {
						/*
					const startMoment = moment(recommendedStartWindow);
					const endMoment = moment(recommendedEndWindow);
					newState.startDate = startMoment.toDate();
					newState.startTime = startMoment.toDate();
					newState.endDate = endMoment.toDate();
					newState.endTime = endMoment.toDate();
					*/

						newState.startDate = recommendedStartWindow;
						newState.startTime = recommendedStartWindow;
						newState.endDate = recommendedEndWindow;
						newState.endTime = recommendedEndWindow;
					} else {
						//- Lets attempt to fill in
						if (primaryStationChanged && lastStationDelivery) {
							const lastStart = moment(lastStationDelivery.deliveryWindowStartDateTime);
							const lastEnd = moment(lastStationDelivery.deliveryWindowEndDateTime);

							const startMoment = moment().add(4, 'days');
							const endMoment = moment().add(4, 'days');
							startMoment.hour(lastStart.hour()).minute(0).second(0);
							endMoment.hour(lastEnd.hour()).minute(0).second(0);

							newState.startDate = startMoment.format('YYYY-MM-DD HH:mm');
							newState.startTime = startMoment.format('YYYY-MM-DD HH:mm');
							newState.endDate = endMoment.format('YYYY-MM-DD HH:mm');
							newState.endTime = endMoment.format('YYYY-MM-DD HH:mm');
						}
					}

					//- Done
					if (recommendedDeliveryVolume) {
						newState.primaryDeliveryAmount = recommendedDeliveryVolume;
					}
					newState.estimate = estimate;
					newState.isFetchingEstimate = false;
					newState.primaryStationChanged = false;

					this.setState(newState);

					return callback();
				},
			],
			(err) => {
				if (err) {
					return showErrors([err]);
				}
			}
		);
	};

	onFirstTerminalChange = (r) => {
		let newState = {
			firstTerminalTerminalId: r.terminalId,
			firstTerminalSupplierId: r.supplierId,
			firstTerminalLoadingNumber: r.loadingNumber,
			firstTerminalSecondaryLoadingNumber: r.secondaryLoadingNumber,
			firstTerminalAccountNumber: r.accountNumber,

			firstTerminalEmailCopyOfDeliveryRequest: r.emailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber: r.useStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads:
				r.requireLoadingNumberPerStationOnSplitLoads,
			firstTerminalUseLoadingAssignments: r.useLoadingAssignments,
			firstTerminalCarrierNeedsToProvidePickupWindow: r.carrierNeedsToProvidePickupWindow,

			firstTerminalBackups: r.backups,
		};

		newState = _.pickBy(newState, (v) => {
			return !_.isUndefined(v);
		});
		if (_.keys(newState).length) {
			this.validateAndUpdate({ newState });
		}
	};

	onSecondTerminalChange = (r) => {
		let newState = {
			secondTerminalTerminalId: r.terminalId,
			secondTerminalSupplierId: r.supplierId,
			secondTerminalLoadingNumber: r.loadingNumber,
			secondTerminalSecondaryLoadingNumber: r.secondaryLoadingNumber,
			secondTerminalAccountNumber: r.accountNumber,

			secondTerminalEmailCopyOfDeliveryRequest: r.emailCopyOfDeliveryRequest,
			secondTerminalUseStationDeliveryIdAsLoadingNumber: r.useStationDeliveryIdAsLoadingNumber,
			secondTerminalRequireLoadingNumberPerStationOnSplitLoads:
				r.requireLoadingNumberPerStationOnSplitLoads,
			secondTerminalUseLoadingAssignments: r.useLoadingAssignments,
			secondTerminalCarrierNeedsToProvidePickupWindow: r.carrierNeedsToProvidePickupWindow,

			secondTerminalBackups: r.backups,
		};
		newState = _.pickBy(newState, (v) => {
			return !_.isUndefined(v);
		});
		if (_.keys(newState).length) {
			this.validateAndUpdate({ newState });
		}
	};

	handleAttachmentUpload = ({ resource }) => {
		const attachmentResourceIds = this.state.attachmentResourceIds.slice();
		/** @namespace resource.resourceId **/
		attachmentResourceIds.push(resource.resourceId);
		this.setState({ attachmentResourceIds });
	};

	handleShowSplitLoad = () => {
		this.setState({ showSplitLoad: true });
	};

	handleHideSplitLoad = () => {
		this.setState({
			showSplitLoad: false,
			secondaryDeliveryAmount: '',
			secondaryStationTank: {},
		});
	};

	closeConfirmDialog = () => {
		this.setState({ isConfirmDialogOpen: false });
	};

	closeUpdatingDialog = () => {
		this.setState({ isUpdatingDialogOpen: false });
	};

	closeCancelDialog = () => {
		this.setState({ isCancelDialogOpen: false });
	};

	handleCancel = () => {
		this.setState({ isCancelDialogOpen: true });
	};

	handleSave = ({ sendAlert }) => {
		const { addDelivery, updateDelivery, showErrors } = this.props;

		//- Required fields
		const formFieldError = {};
		const requiredFields = [
			{ fieldName: 'primaryStationTank', display: 'Station #1' },
			{ fieldName: 'primaryDeliveryAmount', display: 'Delivery amount' },
			{ fieldName: 'startDate', display: 'Start Date' },
			{ fieldName: 'carrierId', display: 'Carrier' },
			{ fieldName: 'firstTerminalTerminalId', display: 'First Terminal' },
		];

		//- Get all the values and do individual checks
		const {
			primaryStationTank,
			primaryDeliveryAmount,
			secondaryStationTank,
			secondaryDeliveryAmount,
			startDate,
			startTime,
			endDate,
			endTime,
			carrierId,

			fuelPickupType,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalAccountNumber,
			firstTerminalProductType,

			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalUseLoadingAssignments,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			secondTerminalTerminalId,
			secondTerminalSupplierId,
			secondTerminalLoadingNumber,
			secondTerminalSecondaryLoadingNumber,
			secondTerminalAccountNumber,

			secondTerminalEmailCopyOfDeliveryRequest,
			secondTerminalUseStationDeliveryIdAsLoadingNumber,
			secondTerminalRequireLoadingNumberPerStationOnSplitLoads,
			secondTerminalUseLoadingAssignments,
			secondTerminalCarrierNeedsToProvidePickupWindow,
			secondTerminalBackups,

			note,
			internalNote,
			attachmentResourceIds,
			showSplitLoad,
			//- for updates
			isUpdate,
			stationDeliveryId,
			allDayWindow,
		} = this.state;

		const primaryStationId = primaryStationTank.stationId;
		const primaryTankNumber = primaryStationTank.tankNumber;
		const primaryProductType = primaryStationTank.productType;

		if (primaryProductType === 'e85' && fuelPickupType === 'dualFuelPickup') {
			requiredFields.push({ fieldName: 'secondTerminalTerminalId', display: 'Second Terminal' });
		}

		if (!this.state.allDayWindow) {
			requiredFields.push(
				{ fieldName: 'startTime', display: 'Start Time' },
				{ fieldName: 'endDate', display: 'End Date' },
				{ fieldName: 'endTime', display: 'End Time' }
			);
		}

		requiredFields.forEach((s) => {
			if (!this.state[s.fieldName]) {
				formFieldError[s.fieldName] = `${s.display} is required`;
			}
		});

		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		let deliveryWindowStartDateTime, deliveryWindowEndDateTime;

		if (allDayWindow) {
			const deliveryWindowStart = moment(startDate).startOf('day').utc();
			const deliveryWindowEnd = moment(startDate).endOf('day').utc();
			deliveryWindowStartDateTime = deliveryWindowStart.format('YYYY-MM-DD HH:mm');
			deliveryWindowEndDateTime = deliveryWindowEnd.format('YYYY-MM-DD HH:mm');
		} else {
			const sd = moment(startDate).format('YYYY-MM-DD');
			const st = moment(startTime).format('HH:mm');
			deliveryWindowStartDateTime = moment(`${sd} ${st}`, 'YYYY-MM-DD HH:mm')
				.utc()
				.format('YYYY-MM-DD HH:mm');

			const ed = moment(endDate).format('YYYY-MM-DD');
			const et = moment(endTime).format('HH:mm');
			deliveryWindowEndDateTime = moment(`${ed} ${et}`, 'YYYY-MM-DD HH:mm')
				.utc()
				.format('YYYY-MM-DD HH:mm');
		}

		const params = {
			primaryStationId,
			primaryTankNumber,
			primaryProductType,
			primaryDeliveryAmount: _.isString(primaryDeliveryAmount)
				? parseInt(primaryDeliveryAmount.replace(/,/g, ''), 10)
				: parseInt(primaryDeliveryAmount, 10),
			deliveryWindowStartDateTime,
			deliveryWindowEndDateTime,
			carrierId,

			firstTerminalTerminalId: parseInt(firstTerminalTerminalId, 10),
			firstTerminalSupplierId: parseInt(firstTerminalSupplierId, 10),
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalAccountNumber,

			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalUseLoadingAssignments,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			note,
			internalNote,
			attachmentResourceIds,
			isHidden: false,
			sendAlert,
			allDayWindow,
		};

		params.fuelPickupType = fuelPickupType ? fuelPickupType : 'singleFuelPickup';

		if (
			primaryProductType === 'e85' &&
			secondTerminalTerminalId &&
			fuelPickupType !== 'singleFuelPickup'
		) {
			params.firstTerminalProductType = firstTerminalProductType;
			params.secondTerminalProductType = 'etoh';

			params.secondTerminalTerminalId = parseInt(secondTerminalTerminalId, 10);
			params.secondTerminalSupplierId = parseInt(secondTerminalSupplierId, 10);
			params.secondTerminalLoadingNumber = secondTerminalLoadingNumber;
			params.secondTerminalSecondaryLoadingNumber = secondTerminalSecondaryLoadingNumber;
			params.secondTerminalAccountNumber = secondTerminalAccountNumber;

			params.secondTerminalEmailCopyOfDeliveryRequest = secondTerminalEmailCopyOfDeliveryRequest;
			params.secondTerminalUseStationDeliveryIdAsLoadingNumber = secondTerminalUseStationDeliveryIdAsLoadingNumber;
			params.secondTerminalRequireLoadingNumberPerStationOnSplitLoads = secondTerminalRequireLoadingNumberPerStationOnSplitLoads;

			params.secondTerminalUseLoadingAssignments = secondTerminalUseLoadingAssignments;
			params.secondTerminalCarrierNeedsToProvidePickupWindow = secondTerminalCarrierNeedsToProvidePickupWindow;
			params.secondTerminalBackups = secondTerminalBackups;
		} else {
			params.firstTerminalProductType = primaryProductType;
		}

		if (showSplitLoad && !_.isEmpty(secondaryStationTank)) {
			if (!secondaryDeliveryAmount) {
				formFieldError.secondaryDeliveryAmount = 'Station #2 delivery amount required';
				this.setState({ formFieldError });
				showErrors(_.values(formFieldError));
				return;
			}

			const secondaryStationId = secondaryStationTank.stationId;
			const secondaryTankNumber = secondaryStationTank.tankNumber;
			const secondaryProductType = secondaryStationTank.productType;

			if (primaryProductType !== secondaryProductType) {
				formFieldError.secondaryDeliveryAmount = 'Station #1 & #2 need to be the same product';
				this.setState({ formFieldError });
				showErrors(_.values(formFieldError));
				return;
			}

			params.secondaryDeliveryAmount = _.isString(secondaryDeliveryAmount)
				? parseInt(secondaryDeliveryAmount.replace(/,/g, ''), 10)
				: parseInt(secondaryDeliveryAmount, 10);

			params.secondaryStationId = secondaryStationId;
			params.secondaryTankNumber = secondaryTankNumber;
			params.secondaryProductType = secondaryProductType;
		} else {
			params.doNotSplitLoad = true;
		}

		if (!isUpdate) {
			async.waterfall(
				[
					(callback) => {
						addDelivery({
							params,
							callback,
						});
					},
					(r, callback) => {
						const confirmingStationDelivery = _.get(r, [
							'collections',
							'stationDeliveryCollection',
							0,
						]);
						if (!confirmingStationDelivery) {
							return callback('Fatal error. Missing stationDelivery.');
						}

						this.showCarrierEmail({
							confirmingStationDelivery,
							callback,
						});
					},
				],
				(e) => {
					if (e) {
						showErrors([e]);
					}
				}
			);
		} else {
			this.setState({
				isUpdatingDialogOpen: true,
				updatingStatus: 'updating',
			});

			params.stationDeliveryId = stationDeliveryId;
			async.waterfall(
				[
					(callback) => {
						updateDelivery({
							params,
							callback,
						});
					},
				],
				(err) => {
					//- errors already show
					if (err) {
						showErrors(err);
						console.error('There was an error', err);
						this.closeUpdatingDialog();
						return;
					}
					if (sendAlert) {
						this.setState({ updatingStatus: 'doneWithAlert' });
					} else {
						this.setState({ updatingStatus: 'done' });
					}
				}
			);
		}
	};

	showCarrierEmail = ({ confirmingStationDelivery, callback }) => {
		const { getStationDeliveryCarrierEmail } = this.props;
		async.waterfall(
			[
				(callback) => {
					getStationDeliveryCarrierEmail({
						params: {
							stationDeliveryIds: [confirmingStationDelivery.stationDeliveryId],
						},
						callback,
					});
				},
				(r, callback) => {
					const confirmingJobResult = _.get(r, ['jobResults', 0]);

					if (!confirmingJobResult) {
						return callback('Did not find Carrier emails.');
					}

					this.setState({
						isConfirmDialogOpen: true,
						confirmingStationDelivery,
						confirmingJobResult,
					});
					return callback();
				},
			],
			callback
		);
	};

	onChange = (event, fieldName, index) => {
		const { showSplitLoad } = this.state;

		const { type, value, checked } = event.target;
		const val = type === 'checkbox' ? checked : value;
		let newState = {};

		if (!_.isNumber(index)) {
			newState = { [fieldName]: val };
		} else {
			const inputs = this.state[fieldName].slice();
			inputs[index] = val;
			newState = { [fieldName]: inputs };
		}

		if (fieldName === 'allDayWindow') {
			if (val === true) {
				newState.startTime = '';
				newState.endTime = '';
				newState.endDate = '';
			}

			newState.isFetchingEstimate = true;
			if (showSplitLoad) {
				newState.isFetchingSecondaryEstimate = true;
			}
		}

		if (fieldName === 'primaryStationTank' && value) {
			newState.primaryStationChanged = true;
			newState.isFetchingEstimate = true;
		}

		if (fieldName === 'secondaryStationTank' && value) {
			newState.secondaryStationChanged = true;
			newState.isFetchingSecondaryEstimate = true;
		}

		if (fieldName === 'primaryDeliveryAmount') {
			const amount = parseInt(value, 10);
			if (amount && amount > 1000 && amount < 9999) {
				newState.isFetchingEstimate = true;
			}
		}

		if (fieldName === 'secondaryDeliveryAmount') {
			const amount = parseInt(value, 10);
			if (amount && amount > 1000 && amount < 9999) {
				newState.isFetchingSecondaryEstimate = true;
			}
		}

		if (fieldName === 'carrierId') {
			this.validateAndUpdate({
				newState: { carrierId: event.target.value },
			});
		}
		this.setState(newState);
	};

	onPureChange = (value, path) => {
		const arrayPath = _.toPath(path);
		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), value);
			newState[basePath] = base;
		} else {
			newState[basePath] = value;
		}

		if (path === 'fuelPickupType') {
			newState.firstTerminalTerminalId = '';
			newState.firstTerminalSupplierId = '';
			newState.firstTerminalBackups = [];
			newState.firstTerminalLoadingNumber = '';
			newState.firstTerminalSecondaryLoadingNumber = '';
			newState.firstTerminalAccountNumber = '';

			newState.firstTerminalEmailCopyOfDeliveryRequest = false;
			newState.firstTerminalUseStationDeliveryIdAsLoadingNumber = false;
			newState.firstTerminalRequireLoadingNumberPerStationOnSplitLoads = false;
			newState.firstTerminalUseLoadingAssignments = false;
			newState.firstTerminalCarrierNeedsToProvidePickupWindow = false;

			newState.secondTerminalTerminalId = '';
			newState.secondTerminalSupplierId = '';
			newState.secondTerminalBackups = [];
			newState.secondTerminalLoadingNumber = '';
			newState.secondTerminalSecondaryLoadingNumber = '';
			newState.secondTerminalAccountNumber = '';

			newState.secondTerminalEmailCopyOfDeliveryRequest = false;
			newState.secondTerminalUseStationDeliveryIdAsLoadingNumber = false;
			newState.secondTerminalRequireLoadingNumberPerStationOnSplitLoads = false;
			newState.secondTerminalUseLoadingAssignments = false;
			newState.secondTerminalCarrierNeedsToProvidePickupWindow = false;
		}

		this.setState(newState);
	};

	onStationTankChange = (value, path) => {
		if (!value) {
			return;
		}

		const newState = { [[path]]: value };
		//const { stationId, tankNumber, productType } = r;

		if (path === 'primaryStationTank') {
			newState.primaryStationChanged = true;
			newState.isFetchingEstimate = true;
		}

		if (path === 'secondaryStationTank') {
			newState.secondaryStationChanged = true;
			newState.isFetchingSecondaryEstimate = true;
		}

		this.setState(newState);
	};

	//- triggers isFetching
	onDateTimeChange = (value, path) => {
		const arrayPath = _.isString(path) ? _.toPath(path) : path;

		const val = _.isObject(value) && value.target ? value.target.value : value;

		const basePath = _.head(arrayPath);
		let base = this.state[basePath];

		const newState = {
			isFetchingEstimate: true,
		};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), val);
			newState[basePath] = base;
		} else {
			newState[basePath] = val;
		}

		if (this.state.showSplitLoad) {
			newState.isFetchingSecondaryEstimate = true;
		}
		this.setState(newState);
	};

	onUnleadedProductTypeChange = (value, path) => {
		if (!value) {
			return;
		}
		const newState = { [[path]]: value };
		newState.firstTerminalTerminalId = '';
		newState.firstTerminalSupplierId = '';
		newState.firstTerminalBackups = [];

		this.setState(newState);
	};

	getFormValuesFromDelivery = (props) => {
		const { stationDelivery } = props;
		if (!stationDelivery) {
			return;
		}

		const formValues = {};

		//- One to one
		[
			'primaryDeliveryAmount',
			'secondaryDeliveryAmount',
			'carrierId',

			'fuelPickupType',
			'firstTerminalTerminalId',
			'firstTerminalSupplierId',
			'firstTerminalLoadingNumber',
			'firstTerminalAccountNumber',
			'firstTerminalSecondaryLoadingNumber',
			'firstTerminalProductType',

			'firstTerminalEmailCopyOfDeliveryRequest',
			'firstTerminalUseStationDeliveryIdAsLoadingNumber',
			'firstTerminalRequireLoadingNumberPerStationOnSplitLoads',
			'firstTerminalUseLoadingAssignments',
			'firstTerminalCarrierNeedsToProvidePickupWindow',
			'firstTerminalBackups',

			'secondTerminalTerminalId',
			'secondTerminalSupplierId',
			'secondTerminalLoadingNumber',
			'secondTerminalAccountNumber',
			'secondTerminalSecondaryLoadingNumber',

			'secondTerminalEmailCopyOfDeliveryRequest',
			'secondTerminalUseStationDeliveryIdAsLoadingNumber',
			'secondTerminalRequireLoadingNumberPerStationOnSplitLoads',
			'secondTerminalUseLoadingAssignments',
			'secondTerminalCarrierNeedsToProvidePickupWindow',
			'secondTerminalBackups',

			'attachmentResourceIds',
			'note',
			'internalNote',
			'allDayWindow',
		].forEach((key) => {
			if (_.isNil(stationDelivery[key])) {
				return;
			}
			formValues[key] = stationDelivery[key];
		});

		const {
			primaryStationId,
			primaryTankNumber,
			primaryProductType,
			secondaryStationId,
			secondaryTankNumber,
			secondaryProductType,
			deliveryWindowStartDateTime,
			deliveryWindowEndDateTime,
			allDayWindow,
		} = stationDelivery;

		//- Stations
		formValues.primaryStationTank = {
			stationId: primaryStationId,
			tankNumber: primaryTankNumber,
			productType: primaryProductType,
		};
		if (secondaryStationId) {
			formValues.secondaryStationTank = {
				stationId: secondaryStationId,
				tankNumber: secondaryTankNumber,
				productType: secondaryProductType,
			};
		}

		//- Dates
		formValues.startDate = deliveryWindowStartDateTime;

		if (!allDayWindow) {
			formValues.startTime = deliveryWindowStartDateTime;
			formValues.endDate = deliveryWindowEndDateTime;
			formValues.endTime = deliveryWindowEndDateTime;
		}

		return formValues;
	};

	addInput = (arrayName) => {
		this.setState({ [arrayName]: this.state[arrayName].concat(['']) });
	};

	removeInput = (arrayName, index) => {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		this.setState({ [arrayName]: inputs });
	};

	//- go through each picked supplier nad make sure this carrier is cleared
	validateAndUpdate = ({ newState }) => {
		const { showErrors, terminals, carriers, supplierMap } = this.props;
		const mergedState = _.merge({}, this.state, newState);
		const {
			primaryProductType,
			carrierId,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			secondTerminalTerminalId,
			secondTerminalSupplierId,
			assignmentStats,
			secondTerminalUseLoadingAssignments,
			//firstTerminalBackups, secondTerminalBackups //XXX not checking on backups
		} = mergedState;

		if (!terminals || !carrierId || !carriers || !supplierMap) {
			this.setState(newState);
			return;
		}

		const errors = [];
		if (firstTerminalTerminalId && firstTerminalSupplierId) {
			const terminal = _.find(terminals, { terminalId: firstTerminalTerminalId });
			const sp = _.find(terminal.supplierProducts, { supplierId: firstTerminalSupplierId });
			if (!sp) {
				return console.error(
					`Missing supplier product : Terminal : ${firstTerminalTerminalId}, ` +
						`supplierId:${firstTerminalSupplierId}.`
				);
			}

			if (!sp.clearedCarrierMap || !sp.clearedCarrierMap[carrierId]) {
				const carrier = _.find(carriers, { carrierId });
				const supplier = supplierMap[firstTerminalSupplierId];
				errors.push(
					`${carrier.name} is not cleared to pick up ${primaryProductType} fuel ` +
						` from ${terminal.name} using supplier ${supplier.name}`
				);
			}
		}

		if (secondTerminalTerminalId && secondTerminalSupplierId) {
			const terminal = _.find(terminals, { terminalId: secondTerminalTerminalId });
			if (!terminal) {
				return console.error(`Missing terminal : ${secondTerminalTerminalId}`);
			}
			const sp = _.find(terminal.supplierProducts, { supplierId: secondTerminalSupplierId });
			if (!sp) {
				return console.error(
					`Missing supplier product : Terminal : ${secondTerminalTerminalId}, ` +
						`supplierId:${secondTerminalSupplierId}.`
				);
			}

			if (!sp.clearedCarrierMap || !sp.clearedCarrierMap[carrierId]) {
				const carrier = _.find(carriers, { carrierId });
				const supplier = supplierMap[secondTerminalSupplierId];
				errors.push(
					`${carrier.name} is not cleared to pick up etoh fuel ` +
						` from ${terminal.name} using supplier ${supplier.name}`
				);
			}
		}

		//- Check to see if we have loading numbers available
		if (secondTerminalUseLoadingAssignments) {
			if (_.isEmpty(assignmentStats)) {
				errors.push('No loading numbers in the system');
			} else {
				let hasAvailable = false;
				assignmentStats.forEach((stat) => {
					const { terminalId, supplierId, available } = stat;

					if (
						terminalId === secondTerminalTerminalId &&
						supplierId === secondTerminalSupplierId &&
						available
					) {
						hasAvailable = true;
					}
				});

				if (!hasAvailable) {
					errors.push('WARNING : No loading numbers available for this terminal / supplier');
				}
			}
		}

		if (errors.length) {
			showErrors(errors);
		}

		this.setState(newState);
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		return _.get(this.state, path, _d);
	};
	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	render() {
		const {
			classes,
			stationDelivery,
			stations,
			carriers,
			terminals,
			resourceMap,
			activateDelivery,
			cancelDelivery,
			updateDelivery,
			history,
			showErrors,
			stationMap,
		} = this.props;

		const {
			isUpdate,
			stationDeliveryId,
			//primaryStationTank,
			attachmentResourceIds,
			showSplitLoad,
			isConfirmDialogOpen,
			isUpdatingDialogOpen,
			isCancelDialogOpen,
			updatingStatus,
			estimate,
			isFetchingEstimate,
			secondaryEstimate,
			isFetchingSecondaryEstimate,
			confirmingStationDelivery,
			confirmingJobResult,
			allDayWindow,
			assignmentStats,
			primaryStationTank,
			secondaryStationTank,
			secondTerminalTerminalId,
			carrierId,
		} = this.state;

		if (!terminals.length || !carriers.length || !stations.length) {
			return <div>Fetching information...</div>;
		}

		if (!assignmentStats.length) {
			return <div>Fetching loading number info..</div>;
		}

		if (isUpdate && !stationDelivery) {
			return <div>Fetching stationDelivery information...</div>;
		}
		const verb = isUpdate ? 'Update' : 'Add';

		// This is strange but for an external logistics may be assigned a station
		// they are not the primary carrier for and still need to see the station
		// in the list JUST for this delivery so we splice it in
		let stationsForDropDown = stations;
		if (_.keys(stationMap).length !== stations.length) {
			stationsForDropDown = _.values(stationMap);
		}

		const resources = [];
		attachmentResourceIds.forEach((resourceId, i) => {
			const resource = resourceMap[resourceId];
			resources.push(resource);
			resource.preview = (
				<div style={{ width: 75, height: 75, backgroundColor: '#ddd' }}>
					<span style={{ fontSize: 14 }}>No preview</span>
					<br />
					<a href={resource.url} target="_blank" rel="noreferrer noopener">
						Download
					</a>
				</div>
			);

			if (!resource.contentType) {
				return;
			}

			/** @namespace resource.contentType **/
			if (resource.contentType.match(/(png|jpg|jpeg|bmp)/)) {
				resource.preview = (
					<img
						alt={`Attachment ${i + 1}`}
						style={{ maxHeight: 175, maxWidth: 175 }}
						src={resource.url}
					/>
				);
			} else if (resource.contentType.match(/(pdf)/)) {
				resource.preview = (
					<object
						data={resource.url}
						style={{ maxHeight: 175, maxWidth: 175 }}
						type="application/pdf"
						width="175"
						height="175"
					>
						<a href={resource.url}>attachment.pdf</a>
					</object>
				);
			}
		});

		const { primaryAmountHelper, secondaryAmountHelper } = this.calculateAmounts();

		let isAllTech = secondTerminalTerminalId === ALL_TECH_TERMINAL_ID;
		let primaryDeliveryNotes = '';
		let primaryDeliveryWarnings = '';
		let carrierWarnings = '';
		const stationLookup = _.keyBy(stations, 'stationId');
		if (primaryStationTank) {
			const currentStation = stationLookup[primaryStationTank.stationId];
			if (currentStation) {
				primaryDeliveryNotes = currentStation.deliveryNotes;
				if (currentStation.creditHoldStartDate) {
					primaryDeliveryWarnings = `This station is currently on a CREDIT HOLD. Any new deliveries should be cleared
					with accounting.`;
				}

				if (carrierId && carrierId !== currentStation.primaryCarrierId) {
					carrierWarnings = `This carrier is not listed as the primary carrier on the station.`;
				}
			}
		}

		let secondaryDeliveryNotes = '';
		let secondaryDeliveryWarnings = '';
		if (secondaryStationTank) {
			const currentStation = stationLookup[secondaryStationTank.stationId];
			if (currentStation) {
				secondaryDeliveryNotes = currentStation.deliveryNotes;

				if (currentStation.creditHoldStartDate) {
					secondaryDeliveryWarnings = `This station is currently on a CREDIT HOLD. Any new deliveries should be cleared
					with accounting.`;
				}

				if (carrierId && carrierId !== currentStation.primaryCarrierId) {
					carrierWarnings += `  This carrier is not listed as the primary carrier on the second station.`;
				}
			}
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography variant="h5" align={'left'} gutterBottom>
								{verb} Delivery {isUpdate && stationDeliveryId}
							</Typography>
						</Grid>
					</Grid>
					<Paper className={classes.paper}>
						<div className={classes.sectionHeader}>Station #1</div>

						<StationTankSelect
							path={'primaryStationTank'}
							label={'Station'}
							controller={this}
							stations={stationsForDropDown}
							onChange={this.onStationTankChange}
							value={this.getValue('primaryStationTank')}
							error={this.getError('primaryStationTank')}
							doNotShowInactive={true}
						/>

						{primaryDeliveryWarnings && (
							<Note type="warn">
								<WarningIcon className={classes.errorIcon} />
								{primaryDeliveryWarnings}
							</Note>
						)}

						{primaryDeliveryNotes && (
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Note>
										<strong style={{ fontSize: '13px' }}>Delivery Note</strong>{' '}
										<pre style={{ margin: 0, paddingLeft: 10 }}>{primaryDeliveryNotes}</pre>
									</Note>
								</Grid>
							</Grid>
						)}

						<Grid container spacing={2}>
							<FMPTextField
								name={'primaryDeliveryAmount'}
								label={'Fuel Delivery Amount'}
								controller={this}
								helpText={primaryAmountHelper}
								md={3}
								contained={false}
							/>
						</Grid>

						<DeliveryEstimate isFetching={isFetchingEstimate} deliveryEstimate={estimate} />

						<div
							className={classes.splitLoadSection}
							style={showSplitLoad ? { maxHeight: 1500 } : { maxHeight: 0 }}
						>
							<div className={classes.sectionHeader}>Station #2</div>
							<StationTankSelect
								path={'secondaryStationTank'}
								label={'Station #2'}
								controller={this}
								stations={stationsForDropDown}
								onChange={this.onStationTankChange}
								value={this.getValue('secondaryStationTank')}
								error={this.getError('secondaryStationTank')}
							/>

							{secondaryDeliveryWarnings && (
								<Note type="warn">
									<WarningIcon className={classes.errorIcon} />
									{secondaryDeliveryWarnings}
								</Note>
							)}

							{secondaryDeliveryNotes && (
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Note>
											<strong style={{ fontSize: '13px' }}>Delivery Note</strong>{' '}
											<pre style={{ margin: 0, paddingLeft: 10 }}>{secondaryDeliveryNotes}</pre>
										</Note>
									</Grid>
								</Grid>
							)}

							<FMPTextField
								name={'secondaryDeliveryAmount'}
								label={'Fuel Delivery Amount'}
								controller={this}
								helpText={secondaryAmountHelper}
							/>

							<DeliveryEstimate
								isFetching={isFetchingSecondaryEstimate}
								deliveryEstimate={secondaryEstimate}
							/>
						</div>

						{!showSplitLoad && (
							<Button
								variant="contained"
								className={classes.addInputButton}
								onClick={this.handleShowSplitLoad}
							>
								SPLIT LOAD
							</Button>
						)}

						{showSplitLoad && (
							<Button
								variant="contained"
								className={classes.addInputButton}
								onClick={this.handleHideSplitLoad}
							>
								DON&apos;T SPLIT LOAD
							</Button>
						)}

						<Grid spacing={2} container>
							<Grid item xs={12} md={3}>
								<div className={classes.sectionHeader}>Delivery Window</div>
							</Grid>
							<Grid item xs={12} md={3}>
								<div className={classes.allDayContainer}>
									<FormControlLabel
										control={
											<Switch
												checked={allDayWindow || false}
												onChange={(e) => this.onChange(e, 'allDayWindow')}
											/>
										}
										label="Use all day window"
									/>
								</div>
							</Grid>
						</Grid>

						<Grid spacing={2} container>
							<Grid item xs={12} md={3}>
								<FMPDatePicker
									label="State Date"
									path={'startDate'}
									onChange={this.onDateTimeChange}
									value={this.getValue('startDate')}
									error={this.getError('startDate')}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<FMPTimePicker
									label="Start Time"
									path={'startTime'}
									onChange={this.onDateTimeChange}
									value={this.getValue('startTime')}
									error={this.getError('startTime')}
									disabled={allDayWindow}
								/>
							</Grid>
						</Grid>

						<Grid spacing={2} container style={{ marginTop: -10 }}>
							<Grid item xs={12} md={3}>
								<FMPDatePicker
									label="End Date"
									path={'endDate'}
									onChange={this.onDateTimeChange}
									value={this.getValue('endDate')}
									error={this.getError('endDate')}
									disabled={allDayWindow}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<FMPTimePicker
									label="End Time"
									path={'endTime'}
									onChange={this.onDateTimeChange}
									value={this.getValue('endTime')}
									error={this.getError('endTime')}
									disabled={allDayWindow}
								/>
							</Grid>
						</Grid>

						<div className={classes.sectionHeader}>Carrier</div>
						<CarrierSelect
							name={'carrierId'}
							label={'Carrier'}
							controller={this}
							carriers={carriers}
						/>

						{carrierWarnings && (
							<Note type="warn">
								<WarningIcon className={classes.warningIcon} />
								{carrierWarnings}
							</Note>
						)}
					</Paper>

					{this.renderProductSection()}

					<Paper className={classes.paper}>
						<FMPRegionHeader isTop={true} label="Email Info" />
						<FMPTextField name={'note'} label={'Notes'} controller={this} multiline={3} />

						{resources.map((resource, i) => {
							return (
								<Grid key={i} container spacing={3} style={{ marginTop: 10 }}>
									<Grid item xs={11} md={4}>
										<div>Attachment #{i + 1}</div>
										{resource.preview}
									</Grid>
									<Grid item xs={11} md={4}>
										{resource.contentType}
									</Grid>
									<Grid item xs={1}>
										<IconButton
											style={{ transform: 'translateY(50%)' }}
											color="primary"
											className={classes.button}
											onClick={() => this.removeInput('attachmentResourceIds', i)}
										>
											<DeleteIcon />
										</IconButton>
									</Grid>
								</Grid>
							);
						})}

						<Grid container spacing={3}>
							<Grid item xs={12} md={6}>
								<ResourceUploader
									name={'attachmentResourceIds'}
									label={'Add attachment'}
									afterUpload={this.handleAttachmentUpload}
								/>
							</Grid>
						</Grid>

						{isAllTech && (
							<div className={classes.allTechAlert}>
								NOTICE : This delivery is using the <strong>All Tech Blend Formula</strong> and thus
								contains out of the ordinary pickup amounts of unleaded and ethanol fuel. However,
								the final blend amount will be standard E85 fuel.
							</div>
						)}
					</Paper>

					<Paper className={classes.paper}>
						<FMPRegionHeader isTop={true} label="Internal" />
						<FMPTextField
							name={'internalNote'}
							label={'Internal Notes For This Delivery (only viewable in FMP)'}
							controller={this}
							multiline={3}
						/>

						{!isUpdate && (
							<div style={{ textAlign: 'right' }}>
								<Button
									variant="contained"
									color="primary"
									className={classes.submitButton}
									onClick={this.handleSave}
								>
									Confirm {verb} Delivery
								</Button>
							</div>
						)}

						{isUpdate && (
							<div style={{ paddingTop: 10, textAlign: 'right' }}>
								<Button
									variant="contained"
									className={classes.addInputButton}
									onClick={this.handleCancel}
								>
									Cancel Delivery
								</Button>

								<span style={{ paddingLeft: 10 }} />

								<Button
									variant="contained"
									//color="primary"
									className={classes.addInputButton}
									onClick={() => {
										this.handleSave({ sendAlert: false });
									}}
								>
									Update : No Change Request
								</Button>

								<span style={{ paddingLeft: 10 }} />

								<Button
									variant="contained"
									color="primary"
									className={classes.submitButton}
									onClick={() => {
										this.handleSave({ sendAlert: true });
									}}
								>
									Update : Send Change Request
								</Button>
							</div>
						)}

						<ConfirmDeliveryDialog
							open={isConfirmDialogOpen}
							onClose={this.closeConfirmDialog}
							activateDelivery={activateDelivery}
							updateDelivery={updateDelivery}
							stationDelivery={confirmingStationDelivery}
							jobResult={confirmingJobResult}
							showErrors={showErrors}
						/>

						<UpdatingDeliveryDialog
							open={isUpdatingDialogOpen}
							onClose={this.closeUpdatingDialog}
							status={updatingStatus}
							history={history}
						/>

						{isUpdate && (
							<ConfirmCancelDeliveryDialog
								open={isCancelDialogOpen}
								onClose={this.closeCancelDialog}
								stationDelivery={stationDelivery}
								cancelDelivery={cancelDelivery}
								showErrors={showErrors}
							/>
						)}
					</Paper>
				</Grid>
			</Grid>
		);
	}

	calculateAmounts = () => {
		const { primaryStationTank, secondTerminalTerminalId, fuelPickupType } = this.state;

		const primaryDeliveryAmount = _.parseInt(this.state.primaryDeliveryAmount);
		const secondaryDeliveryAmount = _.parseInt(this.state.secondaryDeliveryAmount);

		let isAllTech = secondTerminalTerminalId === ALL_TECH_TERMINAL_ID;
		let firstTerminalAmount = '????';
		let secondTerminalAmount = '????';
		let primaryAmountHelper = '';
		let secondaryAmountHelper = '';

		const { productType } = primaryStationTank;

		if (!productType || !primaryDeliveryAmount || primaryDeliveryAmount < 99) {
			return {
				firstTerminalAmount,
				secondTerminalAmount,
				primaryAmountHelper,
				secondaryAmountHelper,
			};
		}

		if (productType === 'e85' && fuelPickupType === 'dualFuelPickup') {
			let calc = calcE85Blend(primaryDeliveryAmount);
			if (isAllTech) {
				calc = calcAllTechBlend(primaryDeliveryAmount);
			}

			primaryAmountHelper = `Blend : ${calc.calculatedEtoh} (etoh) / ${calc.calculatedCarbob} (unl)`;
			let totalDeliveryAmount = primaryDeliveryAmount;
			firstTerminalAmount = calc.calculatedCarbob;
			secondTerminalAmount = calc.calculatedEtoh;

			//- Get second
			if (secondaryDeliveryAmount) {
				let calc = calcE85Blend(secondaryDeliveryAmount);
				if (isAllTech) {
					calc = calcAllTechBlend(secondaryDeliveryAmount);
				}

				secondaryAmountHelper = `Blend : ${calc.calculatedEtoh} (etoh) / ${calc.calculatedCarbob} (unl)`;
				totalDeliveryAmount += secondaryDeliveryAmount;
				primaryAmountHelper += ` -- Total : ${totalDeliveryAmount}`;
				secondaryAmountHelper += ` -- Total : ${totalDeliveryAmount}`;

				firstTerminalAmount += calc.calculatedCarbob;
				secondTerminalAmount += calc.calculatedEtoh;
			}
		} else {
			firstTerminalAmount = primaryDeliveryAmount;
			if (secondaryDeliveryAmount) {
				firstTerminalAmount += secondaryDeliveryAmount;
			}
		}

		return {
			firstTerminalAmount,
			secondTerminalAmount,
			primaryAmountHelper,
			secondaryAmountHelper,
		};
	};

	renderProductSection = () => {
		const { classes } = this.props;

		const { primaryStationTank } = this.state;

		const { productType } = primaryStationTank;

		return (
			<Paper className={classes.paper}>
				<FMPRegionHeader isTop={true} label="Fuel Info" />

				{!productType && (
					<div className={classes.pickFuelType}>
						Please first pick a station and tank and then the correct fuel fields will be available.
					</div>
				)}

				{productType && this.renderProductFields({ productType })}
			</Paper>
		);
	};

	renderProductFields = ({ productType }) => {
		const { classes } = this.props;

		const { fuelPickupType } = this.state;

		const e85BlendOptions = [
			{ display: 'Pickup E85 (single fuel pickup)', value: 'singleFuelPickup' },
			{ display: 'Pickup UNL & ETOH (dual fuel pickup)', value: 'dualFuelPickup' },
		];

		if (productType === 'e85') {
			return (
				<Fragment>
					<FMPSelect
						label="E85 fuel pickup option"
						options={e85BlendOptions}
						path={'fuelPickupType'}
						onChange={this.onPureChange}
						value={this.getValue('fuelPickupType')}
						error={this.getError('fuelPickupType')}
						md={6}
					/>

					{!fuelPickupType && (
						<div className={classes.pickFuelType}>Pick a pickup blend type for the E85 fuel.</div>
					)}

					{fuelPickupType === 'singleFuelPickup' && this.renderSingleTerminalPickup('e85')}

					{fuelPickupType === 'dualFuelPickup' &&
						this.renderDualTerminalPickup({
							firstProductType: 'unl',
							secondProductType: 'etoh',
						})}
				</Fragment>
			);
		} else {
			return this.renderSingleTerminalPickup(productType);
		}
	};

	renderSingleTerminalPickup = (productType) => {
		const { classes, terminals, supplierMap } = this.props;

		const {
			stationDeliveryId,
			showSplitLoad,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalAccountNumber,
			//firstTerminalProductType,
			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			formFieldError,
		} = this.state;

		const { firstTerminalAmount } = this.calculateAmounts();

		const fuelDisplay = valueToDisplay(productType);
		return (
			<Fragment>
				<div className={classes.sectionHeader}>{fuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{firstTerminalAmount}</strong> {fuelDisplay} gallons from:
					</div>
					<TerminalSupplierPicker
						label={`Terminal for ${fuelDisplay}`}
						error={formFieldError['firstTerminalTerminalId']}
						terminalId={firstTerminalTerminalId}
						supplierId={firstTerminalSupplierId}
						loadingNumber={firstTerminalLoadingNumber}
						secondaryLoadingNumber={firstTerminalSecondaryLoadingNumber}
						accountNumber={firstTerminalAccountNumber}
						emailCopyOfDeliveryRequest={firstTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={firstTerminalUseStationDeliveryIdAsLoadingNumber}
						requireLoadingNumberPerStationOnSplitLoads={
							firstTerminalRequireLoadingNumberPerStationOnSplitLoads
						}
						carrierNeedsToProvidePickupWindow={firstTerminalCarrierNeedsToProvidePickupWindow}
						backups={firstTerminalBackups}
						onChange={this.onFirstTerminalChange}
						terminals={terminals}
						supplierMap={supplierMap}
						productTypes={[productType]}
						stationDeliveryId={stationDeliveryId}
						isSplitLoad={showSplitLoad}
					/>
				</div>
			</Fragment>
		);
	};

	renderDualTerminalPickup = ({ firstProductType, secondProductType }) => {
		const { classes, terminals, supplierMap } = this.props;

		const {
			stationDeliveryId,
			showSplitLoad,
			firstTerminalTerminalId,
			firstTerminalSupplierId,
			firstTerminalLoadingNumber,
			firstTerminalSecondaryLoadingNumber,
			firstTerminalAccountNumber,
			firstTerminalProductType,
			firstTerminalEmailCopyOfDeliveryRequest,
			firstTerminalUseStationDeliveryIdAsLoadingNumber,
			//firstTerminalRequireLoadingNumberPerStationOnSplitLoads,
			firstTerminalCarrierNeedsToProvidePickupWindow,
			firstTerminalBackups,

			secondTerminalTerminalId,
			secondTerminalSupplierId,
			secondTerminalLoadingNumber,
			secondTerminalSecondaryLoadingNumber,
			secondTerminalAccountNumber,
			secondTerminalEmailCopyOfDeliveryRequest,
			secondTerminalUseStationDeliveryIdAsLoadingNumber,
			secondTerminalRequireLoadingNumberPerStationOnSplitLoads,
			secondTerminalUseLoadingAssignments,
			secondTerminalCarrierNeedsToProvidePickupWindow,
			secondTerminalBackups,
			formFieldError,
		} = this.state;

		const { firstTerminalAmount, secondTerminalAmount } = this.calculateAmounts();

		const firstFuelDisplay = valueToDisplay(firstProductType);
		const secondFuelDisplay = valueToDisplay(secondProductType);

		return (
			<Fragment>
				<div className={classes.sectionHeader}>{firstFuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{firstTerminalAmount}</strong> unleaded gallons from:
					</div>

					<FMPProductSelect
						productTypes={['unl', 'midunl', 'preunl']}
						label="Select unleaded fuel type"
						path={'firstTerminalProductType'}
						onChange={this.onUnleadedProductTypeChange}
						value={this.getValue('firstTerminalProductType')}
						error={this.getError('firstTerminalProductType')}
						md={6}
					/>

					<TerminalSupplierPicker
						label={`Terminal for ${firstFuelDisplay}`}
						error={formFieldError['firstTerminalTerminalId']}
						terminalId={firstTerminalTerminalId}
						supplierId={firstTerminalSupplierId}
						loadingNumber={firstTerminalLoadingNumber}
						secondaryLoadingNumber={firstTerminalSecondaryLoadingNumber}
						accountNumber={firstTerminalAccountNumber}
						productTypes={[firstTerminalProductType || 'unl']}
						emailCopyOfDeliveryRequest={firstTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={firstTerminalUseStationDeliveryIdAsLoadingNumber}
						carrierNeedsToProvidePickupWindow={firstTerminalCarrierNeedsToProvidePickupWindow}
						backups={firstTerminalBackups}
						onChange={this.onFirstTerminalChange}
						terminals={terminals}
						supplierMap={supplierMap}
						stationDeliveryId={stationDeliveryId}
						isSplitLoad={showSplitLoad}
					/>
				</div>

				<div className={classes.sectionHeader}>{secondFuelDisplay} Pickup</div>
				<div className={classes.terminalSection}>
					<div className={classes.pickup}>
						Pickup <strong>{secondTerminalAmount}</strong> ethanol gallons from:
					</div>
					<TerminalSupplierPicker
						label={`Terminal for ${secondFuelDisplay}`}
						error={formFieldError['secondTerminalTerminalId']}
						terminalId={secondTerminalTerminalId}
						supplierId={secondTerminalSupplierId}
						loadingNumber={secondTerminalLoadingNumber}
						secondaryLoadingNumber={secondTerminalSecondaryLoadingNumber}
						accountNumber={secondTerminalAccountNumber}
						emailCopyOfDeliveryRequest={secondTerminalEmailCopyOfDeliveryRequest}
						useStationDeliveryIdAsLoadingNumber={secondTerminalUseStationDeliveryIdAsLoadingNumber}
						requireLoadingNumberPerStationOnSplitLoads={
							secondTerminalRequireLoadingNumberPerStationOnSplitLoads
						}
						useLoadingAssignments={secondTerminalUseLoadingAssignments}
						carrierNeedsToProvidePickupWindow={secondTerminalCarrierNeedsToProvidePickupWindow}
						backups={secondTerminalBackups}
						terminals={terminals}
						supplierMap={supplierMap}
						productTypes={['etoh']}
						onChange={this.onSecondTerminalChange}
						stationDeliveryId={stationDeliveryId}
						isSplitLoad={showSplitLoad}
					/>
				</div>
			</Fragment>
		);
	};
}

SaveStationDelivery.propTypes = {
	classes: PropTypes.object.isRequired,
	getDelivery: PropTypes.func.isRequired,
	addDelivery: PropTypes.func.isRequired,
	activateDelivery: PropTypes.func.isRequired,
	updateDelivery: PropTypes.func.isRequired,
	cancelDelivery: PropTypes.func.isRequired,
	getStationDeliveryCarrierEmail: PropTypes.func.isRequired,
	estimateDelivery: PropTypes.func.isRequired,

	stationDelivery: PropTypes.object,

	searchStations: PropTypes.func.isRequired,
	stations: PropTypes.array,

	searchCarriers: PropTypes.func.isRequired,
	carriers: PropTypes.array,

	searchTerminals: PropTypes.func.isRequired,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,

	getLoadingAssignmentStats: PropTypes.func,
	showErrors: PropTypes.func.isRequired,
	history: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles),
	LoadingAssignmentsContainer,
	DeliveryContainer,
	StationsContainer,
	CarriersContainer,
	TerminalsContainer,
])(SaveStationDelivery);
